/* eslint-disable no-unused-vars */
import React from 'react';
import { getWidthChart, convertMinutesToPixel, convertToInt } from '../../utils/common';
import { ACTION_ROUTE, COLOR_DEFAULT, STEP_TYPE, TIME_FORMAT } from '../../constants';
import { Popover } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { Draggable } from '@hello-pangea/dnd';
import StepContent from './StepContent';
import map from 'lodash/map';
import Icon from '../../icons';
import moment from 'moment';

const marginLeftAdjustment = {
  300: 0,
  600: 10,
  900: 20,
  1200: 30,
  1500: 40,
};

const DriverListSteps = ({
  route,
  startTime,
  oneHourPx,
  color,
  solutions,
  stepCompleted,
  steps,
  minutes,
  handleCloseStepPopup,
  onClickStep,
  routePlan,
  selectedMultipleSteps,
  multipleStepState,
  driverId,
  selectedStep,
  // OVO day-plan planned/actual split: a started route renders two stacked
  // rows. `timeMode='planned'` anchors the shift line on the PLANNED boundaries
  // (never the actual badged ones); `readOnly` renders non-draggable badges
  // (the planned reference is informational); `solutionKey` selects which
  // entry of `solutions` holds this row's positioned steps (so the planned row
  // reads its own marginLefts, not the actual row's).
  timeMode = 'actual',
  readOnly = false,
  solutionKey,
}) => {
  const isPlanned = timeMode === 'planned';
  const useActualShift = !isPlanned && route.actionStatus === ACTION_ROUTE.COMPLETED;
  const shiftStart = useActualShift
    ? route.actualShiftStart
      ? moment(route.actualShiftStart).format(TIME_FORMAT)
      : route.shiftStart
    : route.shiftStart;
  const shiftEnd = useActualShift
    ? route.actualShiftEnd
      ? moment(route.actualShiftEnd).format(TIME_FORMAT)
      : route.shiftEnd
    : route.shiftEnd;

  let actualShiftStart, widthActualShiftStart;

  if (!isPlanned && route.actionStatus !== ACTION_ROUTE.COMPLETED && route.actualShiftStart) {
    actualShiftStart = moment(route.actualShiftStart).format(TIME_FORMAT);
    widthActualShiftStart = getWidthChart(startTime, actualShiftStart, oneHourPx);
  }

  const widthStart = getWidthChart(startTime, shiftStart, oneHourPx);
  const widthEnd = getWidthChart(startTime, shiftEnd, oneHourPx);
  const widthRoad = getWidthChart(shiftStart, shiftEnd, oneHourPx);
  const adjustedMarginLeft = marginLeftAdjustment[oneHourPx] || 0;

  const defaultColor = COLOR_DEFAULT;
  const stepsDone = stepCompleted ? stepCompleted[route.driver.id] : [];
  // Which `solutions` entry holds this row's positioned steps (planned vs actual).
  const resolvedSolutionKey = solutionKey || route.driver.id;

  const getDurationStep = (step) => {
    if (step.arrivalTime && step.finishTime) {
      return moment(step.finishTime, TIME_FORMAT).diff(moment(step.arrivalTime, TIME_FORMAT), 'minutes');
    }
    return typeof step.duration === 'number' ? step.duration : 10;
  };

  return (
    <div className="driver-row">
      <Popover
        open={false}
        content={
          <div>
            {route.startAddress}
            <div className="actions">
              <span style={{ margin: 0 }}>
                Show more <ArrowRightOutlined />
              </span>
            </div>
          </div>
        }
      >
        <div
          className="truck-start"
          onClick={() =>
            onClickStep &&
            onClickStep({
              id: `${route.id}-start`,
              type: 'vehicle',
            })
          }
          // Shift the truck-start fully LEFT of the route line so it
          // doesn't sit on top of the first step badge when the first
          // stop is scheduled right at shift-start time. Truck icon ~34px
          // wide; -40 puts its right edge clear of the step's bounds.
          // Clamp at 0 so when the shift starts at the very left of the axis
          // (widthStart ≈ 0) the icon stays inside the track instead of
          // sliding under the driver-info column and vanishing.
          style={{ left: Math.max(widthStart - 40, 0) }}
        >
          <Icon type={'RouteDetailsStart'} color={'#' + (color || defaultColor)} />
        </div>
      </Popover>
      <div
        className="line"
        style={{
          width: widthRoad,
          left: widthStart,
          background: `#${color || defaultColor}`,
        }}
      ></div>
      {actualShiftStart && (
        <div
          className="truck-start"
          onClick={() =>
            onClickStep &&
            onClickStep({
              id: `${route.id}-start`,
              type: 'vehicle',
            })
          }
          style={{ left: Math.max(widthActualShiftStart - 40, 0) }}
        >
          <Icon type={'RouteDetailsStart'} color={'#ffab00'} />
        </div>
      )}
      <Popover
        open={false}
        content={
          <div>
            {route.endAddress}
            <div className="actions">
              <span
                // onClick={() => setShowMoreRoute(true)}
                style={{ margin: 0 }}
              >
                Show more <ArrowRightOutlined />
              </span>
            </div>
          </div>
        }
      >
        <div
          className="truck-end"
          onClick={() =>
            onClickStep &&
            onClickStep({
              // Always open the Route End card. Routes that finish back
              // at the hub have no distinct endAddress/endAddressLat, but
              // StopClicked already falls back to startAddress for the
              // location while keeping the "Route End" header + finish
              // time. The old `-start` fallback here meant clicking the
              // end icon wrongly showed the Route Start card. This also
              // matches RouteMap, which always emits `${id}-end`.
              id: `${route.id}-end`,
              type: 'vehicle',
            })
          }
          // Shifted slightly past `widthEnd` (the actualShiftEnd time) so
          // step badges that land within the same minute as shift-end —
          // typical of OVO-003 multi-delivery merges where all stops
          // share the same DELIVERY_ARRIVED timestamp — don't visually
          // escape past the end icon. Old offset was -11 (icon centered
          // on widthEnd); +8 puts the icon's left edge a few px after
          // the line ends.
          style={{ left: widthEnd + 8 }}
        >
          <Icon type={'RouteDetailsEnd'} color={'#' + (color || defaultColor)} />
        </div>
      </Popover>
      {steps.length > 0 && (
        <div className="steps">
          {map(steps, (step, indexStep) => {
            const keyDraggable = `draggable${step.id}`;
            const stepPopover = `StepPopover-${step.id}-${step.type.toLowerCase()}`;
            const width = step.width;
            const widthDuration = convertMinutesToPixel(getDurationStep(step), oneHourPx);
            const stepSolution = solutions[resolvedSolutionKey]?.find((rs) => rs.stepId === step.stepId) || {};

            let isCompleted = step.status === 'COMPLETED' || step.orderStatus === 'COMPLETED';

            const endShift = convertToInt(shiftEnd);
            const endTime = convertToInt(step.endTime);
            const finishTime = convertToInt(step.finishTime);
            const arrivalTimeSecs = convertToInt(step.arrivalTime);

            const startTimeInt = convertToInt(step.startTime);
            const isLate =
              (finishTime >= 0 && endTime >= 0 && finishTime > endTime) ||
              (finishTime >= 0 && endShift >= 0 && finishTime > endShift) ||
              (step.endTime && arrivalTimeSecs > endTime);
            const isEarly = startTimeInt >= 0 && arrivalTimeSecs >= 0 && arrivalTimeSecs < startTimeInt;

            if (step.type === STEP_TYPE.pickup) {
              const orderIds = step.orderIds || [];
              const routeSteps = route.steps || [];
              routeSteps.forEach((routeStep) => {
                if (isCompleted || routeStep.type === STEP_TYPE.delivery) return;
                // Check if any orderId in routeStep matches any orderId in step.orderIds
                const routeStepOrderIds = routeStep.orderIds || (routeStep.orderId ? [routeStep.orderId] : []);
                if (routeStep.status !== 'IN_PROGRESS' && orderIds.some((id) => routeStepOrderIds.includes(id))) {
                  isCompleted = true;
                }
              });
            }

            const stepMarginLeft = stepSolution.marginLeft + (indexStep === 0 ? widthStart : adjustedMarginLeft);
            const stepZIndex = selectedStep.id === step.id && selectedStep.type === step.type ? 99999 : 10;
            const stepContent = (
              <StepContent
                step={step}
                routePlan={routePlan}
                route={route}
                indexStep={indexStep + (stepsDone?.length || 0)}
                color={'#' + color}
                stepPopover={stepPopover}
                widthDuration={widthDuration}
                handleCloseStepPopup={handleCloseStepPopup}
                onClickStep={onClickStep}
                selectedMultipleSteps={selectedMultipleSteps}
                multipleStepState={multipleStepState}
                driverId={driverId}
                selectedStep={selectedStep}
                selectable={!readOnly}
                isLate={isLate}
                isEarly={isEarly}
              />
            );

            // Planned reference row: non-draggable plain badges (no dnd context).
            if (readOnly) {
              return (
                <div key={keyDraggable} style={{ width: width, zIndex: stepZIndex, marginLeft: stepMarginLeft }}>
                  {stepContent}
                </div>
              );
            }

            return (
              <Draggable key={keyDraggable} draggableId={keyDraggable} index={indexStep} isDragDisabled={isCompleted}>
                {(provided) => {
                  return (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={{
                        width: width,
                        zIndex: stepZIndex,
                        marginLeft: stepMarginLeft,
                        ...provided.draggableProps.style,
                      }}
                    >
                      {stepContent}
                    </div>
                  );
                }}
              </Draggable>
            );
          })}
        </div>
      )}
      {/* <div className="stepsDone">
        {map(stepsDone, (step, indexStep) => {
          // const indexStep = steps.length + indexStepDone - 1;
          const keyDraggable = `draggable${step.id}`;
          const stepPopover = `StepPopover-${step.id}-${step.type.toLowerCase()}`;
          const width = step.width;
          const widthDuration = convertMinutesToPixel(getDurationStep(step), oneHourPx);
          const endShift = convertToInt(shiftEnd);
          const endTime = convertToInt(step.endTime);
          const finishTime = convertToInt(step.finishTime);
          const arrivalTimeSecs = convertToInt(step.arrivalTime);

          const isLate =
            (finishTime >= 0 && endTime >= 0 && finishTime > endTime) ||
            (finishTime >= 0 && endShift >= 0 && finishTime > endShift) ||
            (stop.endTime && arrivalTimeSecs > endTime);

          let isCompleted = true;

          if (step.type === STEP_TYPE.pickup) {
            const orderIds = step.orderIds || [];
            const routeSteps = route.steps || [];
            routeSteps.forEach((routeStep) => {
              if (isCompleted || routeStep.type === STEP_TYPE.delivery) return;
              // Check if any orderId in routeStep matches any orderId in step.orderIds
              const routeStepOrderIds = routeStep.orderIds || (routeStep.orderId ? [routeStep.orderId] : []);
              if (routeStep.status !== 'IN_PROGRESS' && orderIds.some((id) => routeStepOrderIds.includes(id))) {
                isCompleted = true;
              }
            });
          }

          return (
            <Draggable key={keyDraggable} draggableId={keyDraggable} index={indexStep} isDragDisabled={true}>
              {(provided) => {
                return (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    style={{
                      width: width,
                      minWidth: 12,
                      zIndex: selectedStep.id === step.id && selectedStep.type === step.type ? 99999 : 10,
                      marginLeft: step.marginLeft + (indexStep === 0 ? widthStart : 0),
                      ...provided.draggableProps.style,
                    }}
                  >
                    <StepContent
                      step={step}
                      routePlan={routePlan}
                      route={route}
                      indexStep={indexStep}
                      color={'#' + color}
                      stepPopover={stepPopover}
                      widthDuration={widthDuration}
                      onHoverStep={onHoverStep}
                      handleCloseStepPopup={handleCloseStepPopup}
                      onClickStep={onClickStep}
                      selectedStep={selectedStep}
                    />
                  </div>
                );
              }}
            </Draggable>
          );
        })}
      </div> */}
      <div className="all-minutes">
        {minutes.map((minute, index) => {
          return <div className="minutes" key={index} style={{ width: `${oneHourPx / 2}px` }}></div>;
        })}
      </div>
    </div>
  );
};

export default DriverListSteps;
